///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2023 - 2024
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { includes, isEmpty } from "lodash";

import PanelStateIndicator from "./PanelStateIndicator";
import messages from "../../helpers/messages";
import {
  mode as modeType,
  capacityMatchingStatus as capacityMatchingStatusType,
  userRole
} from "../../helpers/types";
import {
  changeCapacityMatchesPagination,
  fetchCapacityMatchLaneSummary,
  fetchCarrierMatches,
  resetState,
  setCapacityMatchCarriersInfo,
  enableBulkMatchingOperation,
  updateStep
} from "../../slices/bidProcurementSlice";
import { fetchBidSummary } from "../../slices/bidSummarySlice";
import CapacityMatchLoading from "./procurement/CapacityMatchLoading";
import CapacityMatchTableBody from "./procurement/CapacityMatchTableBody";
import CapacityMatchTableHeader from "./procurement/CapacityMatchTableHeader";
import CarrierMatchingDrawer from "./procurement/CarrierMatchingDrawer";
import NoCapacityMatchLaneSummary from "./procurement/NoCapacityMatchLaneSummary";
import Toolbar from "./procurement/Toolbar";
import MatchOverwriteModal from "./procurement/MatchOverwriteModal";
import reverseMapLaneCarrierCapacityMatch from "../../helpers/reverseMapLaneCarrierCapacityMatch";
import Pagination from "../form-controls/Pagination";

const initialDetails = { open: false, laneIds: "" };

export const Procurement = ({ bidId, role }) => {
  const dispatch = useDispatch();
  const {
    bidProcurement: {
      capacityMatchLaneSummary: {
        isFetching,
        isError,
        capacityMatches,
        perPage,
        offset,
        totalFiltered
      },
      rowsSelected,
      copyMatchesFromRow,
      isCapacityMatchingInProgress
    },
    bidSummary: { summary }
  } = useSelector(state => state);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [openMatchOverwriteModalDetails, setOpenMatchOverwriteModalDetails] =
    useState(initialDetails);

  useEffect(() => {
    dispatch(fetchBidSummary({ bidId, role }));
    dispatch(fetchCapacityMatchLaneSummary(bidId));

    return function cleanup() {
      dispatch(resetState());
    };
  }, [dispatch, bidId, role]);

  const copyMatchesToLanes = () => {
    dispatch(
      fetchCarrierMatches(
        bidId,
        () => {
          const { laneMatches, isNoneOptionSelected } =
            reverseMapLaneCarrierCapacityMatch(
              capacityMatches,
              rowsSelected,
              copyMatchesFromRow
            );
          dispatch(
            setCapacityMatchCarriersInfo({
              laneMatches,
              isNoneOptionSelected,
              isCopyMatch: true
            })
          );
          !isNoneOptionSelected && dispatch(updateStep(2));
        },
        true
      )
    );
  };

  const openCarrierMatchingDrawer = isBulkMatching => {
    const selectedLanes = capacityMatches?.filter(x =>
      includes(rowsSelected, x.truckloadLaneId)
    );
    const lanesContainingCarrierMatches = selectedLanes.filter(
      x => !isEmpty(x.laneMatches)
    );

    if (lanesContainingCarrierMatches?.length > 0 && !isBulkMatching) {
      const laneIds = lanesContainingCarrierMatches
        .map(x => x.shipperLaneId)
        .join(", ");
      setOpenMatchOverwriteModalDetails({
        open: true,
        laneIds: laneIds
      });
    } else {
      setOpenDrawer(true);
      if (isBulkMatching) {
        dispatch(enableBulkMatchingOperation(true));
        dispatch(fetchCarrierMatches(bidId));
      } else {
        copyMatchesToLanes();
      }
    }
  };

  const handleOverwriteAndContinue = () => {
    setOpenMatchOverwriteModalDetails(initialDetails);
    setOpenDrawer(true);
    copyMatchesToLanes();
  };

  const capacityMatchingStatus = summary?.modes.find(
    ({ mode }) => mode === modeType.tl
  )?.capacityMatchingStatus;

  const isPendingOrRejected =
    capacityMatchingStatus === capacityMatchingStatusType.pending ||
    capacityMatchingStatus === capacityMatchingStatusType.rejected ||
    capacityMatchingStatus === capacityMatchingStatusType.undefined;

  const isSubmitted =
    capacityMatchingStatus === capacityMatchingStatusType.submitted;

  const isReviewForCarrierReps =
    capacityMatchingStatus === capacityMatchingStatusType.review &&
    role === userRole.carrierRelationsRepresentative;

  return (
    <div className="bid procurement" id="procurement">
      {isError ? (
        <div
          style={{
            height: window.screen.height - 290
          }}
          className="panel"
        >
          <div className="m-0">
            <PanelStateIndicator
              error={isError}
              errorMessage={messages.pleaseRefreshThePage.defaultMessage}
            />
          </div>
        </div>
      ) : (
        <div>
          {isFetching || (capacityMatches && capacityMatches.length) ? (
            <>
              <Toolbar
                openCarrierMatchingDrawer={openCarrierMatchingDrawer}
                bidId={bidId}
              />
              {isFetching ? (
                <div
                  className="scroll table-width"
                  style={{ maxHeight: window.screen.height - 240 }}
                >
                  <CapacityMatchLoading />
                </div>
              ) : (
                <>
                  <div
                    className="scroll table-width"
                    style={{ maxHeight: window.screen.height - 240 }}
                  >
                    <table
                      className="one-whole capacity-match-grid"
                      id="capacity-match-grid"
                    >
                      <CapacityMatchTableHeader
                        isPendingOrRejected={isPendingOrRejected}
                        isSubmitted={isSubmitted}
                        isReviewForCarrierReps={isReviewForCarrierReps}
                        isCapacityMatchingInProgress={
                          isCapacityMatchingInProgress
                        }
                      />
                      <CapacityMatchTableBody
                        isPendingOrRejected={isPendingOrRejected}
                        isSubmitted={isSubmitted}
                        isReviewForCarrierReps={isReviewForCarrierReps}
                        isCapacityMatchingInProgress={
                          isCapacityMatchingInProgress
                        }
                        bidId={bidId}
                        setOpenDrawer={setOpenDrawer}
                      />
                    </table>
                  </div>
                  <div className="one-whole p-20 fr">
                    <Pagination
                      onChange={paginationDetails => {
                        dispatch(
                          changeCapacityMatchesPagination(paginationDetails)
                        );
                      }}
                      limit={perPage}
                      offset={offset}
                      total={totalFiltered}
                      perPageOptions={[25, 50, 100, 200]}
                    />
                  </div>
                </>
              )}
            </>
          ) : (
            <NoCapacityMatchLaneSummary />
          )}
        </div>
      )}
      <CarrierMatchingDrawer
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        bidId={bidId}
      />
      <MatchOverwriteModal
        {...openMatchOverwriteModalDetails}
        handleClose={() => setOpenMatchOverwriteModalDetails(initialDetails)}
        handleOverwriteAndContinue={handleOverwriteAndContinue}
      />
    </div>
  );
};

export default Procurement;
